import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CofaKind,
  CofaLookupResponse,
  CofaStats,
  LookupQuery,
} from './types'

const BASE = '/api/procurement'

export function useCofaDocuments(query: LookupQuery) {
  return useQuery({
    queryKey: ['cofa-documents', 'lookup', query],
    queryFn: () => {
      const params = new URLSearchParams({
        q: query.q,
        status: query.status,
        identity: query.identity ?? 'all',
        limit: String(query.limit),
        offset: String(query.offset),
        scope: query.scope ?? 'in_stock',
      })
      return jsonFetch<CofaLookupResponse>(
        `${BASE}/cofa-lookup?${params.toString()}`,
      )
    },
    staleTime: 15_000,
  })
}

export function useCofaStats() {
  return useQuery({
    queryKey: ['cofa-documents', 'stats'],
    queryFn: () => jsonFetch<CofaStats>(`${BASE}/cofa-stats`),
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

export interface RenameVars {
  lotId: number
  kind: CofaKind
  newFilename: string
}

export function useRenameCofa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: RenameVars) =>
      jsonFetch<unknown>(`${BASE}/cofa/rename`, {
        method: 'PATCH',
        body: {
          lot_id: vars.lotId,
          kind: vars.kind,
          new_filename: vars.newFilename,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    },
  })
}

export interface RemoveVars {
  lotId: number
  kind: CofaKind
}

export function useRemoveCofa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: RemoveVars) =>
      jsonFetch<unknown>(`${BASE}/cofa`, {
        method: 'DELETE',
        body: { lot_id: vars.lotId, kind: vars.kind },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    },
  })
}

export interface ReplaceVars {
  file: File
  lotId: number
  kind: CofaKind
}

export function useReplaceCofa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: ReplaceVars) => {
      const form = new FormData()
      form.append('file', vars.file)
      form.append('lot_id', String(vars.lotId))
      form.append('kind', vars.kind)
      return jsonFetch<unknown>(`${BASE}/cofa/replace`, {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    },
  })
}


// ─── 2026-05-01: Reassign CofA product ────────────────────────────
//
// Backend: POST /api/procurement/cofa/lot/{lot_id}/reassign-product
// Updates Odoo `stock.lot.product_id`, the local pipeline rows, and
// posts chatter on both old and new product records. Hard-fails when
// stock moves already reference the lot under the old product.

export interface ReassignProductResponse {
  ok: boolean
  lot_id: number
  lot_name: string
  product_id: number
  product_name: string
  old_product_id: number | null
  old_product_name: string
  pipeline_rows_updated?: number
  pipeline_id?: number
  noop?: boolean
}

/** 409 detail when the new product conflicts with the vendor CofA or the
 *  vendor's open PO (2026-10-16, lot FPD-260904-50N). Resend with
 *  `acknowledge_conflict: true` to reassign anyway; the CofA then stays a
 *  mismatch until regulatory verifies it. */
export interface VendorCofaConflict {
  code: 'vendor_cofa_conflict'
  reason: string
  printed: string
  po_supported: boolean
}

export function vendorCofaConflictOf(err: unknown): VendorCofaConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null
  const d = err.detail as Partial<VendorCofaConflict> | undefined
  return d && d.code === 'vendor_cofa_conflict' ? (d as VendorCofaConflict) : null
}

export function useReassignCofaProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      lot_id: number
      product_id: number
      reason?: string | null
      acknowledge_conflict?: boolean
    }) =>
      jsonFetch<ReassignProductResponse>(
        `${BASE}/cofa/lot/${vars.lot_id}/reassign-product`,
        {
          method: 'POST',
          body: {
            product_id: vars.product_id,
            reason: vars.reason ?? null,
            acknowledge_conflict: vars.acknowledge_conflict ?? false,
          },
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
      void qc.invalidateQueries({ queryKey: ['cofa-pipeline'] })
      void qc.invalidateQueries({ queryKey: ['cofa-stats'] })
      void qc.invalidateQueries({ queryKey: ['cofa-lookup'] })
    },
  })
}

// ─── 2026-09-02: Delete lot ────────────────────────────────────────
//
// Backend: DELETE /api/procurement/cofa/lot/{lot_id}
// Permanently deletes the Odoo stock.lot record (unlike useRemoveCofa,
// which only clears the attachment fields). Hard-blocked with a 409 if
// the lot has any real stock activity — only ever removes phantom lots
// created by a wrong-product CofA match.

export interface DeleteLotResponse {
  ok: boolean
  lot_id: number
  lot_name: string
  product_name: string
  pipeline_rows_deleted: number
}

export function useDeleteCofaLot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { lotId: number }) =>
      jsonFetch<DeleteLotResponse>(`${BASE}/cofa/lot/${vars.lotId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
      void qc.invalidateQueries({ queryKey: ['cofa-pipeline'] })
      void qc.invalidateQueries({ queryKey: ['cofa-stats'] })
      void qc.invalidateQueries({ queryKey: ['cofa-lookup'] })
    },
  })
}

// Lookup of products SPI buys from a given vendor — feeds the
// ChangeProductDrawer's picker. Shared with the upload-flow's
// disambiguation picker (procurement_cofa.py:vendor_products).

export interface VendorProduct {
  id: number
  name: string
  default_code: string
}

export interface VendorProductsResponse {
  vendor_products: VendorProduct[]
  other_products: VendorProduct[]
}

export function useVendorProducts(
  vendorId: number | null,
  query: string = '',
) {
  // Defensive: Odoo m2o "not set" can leak as boolean `false` despite
  // the type annotation (see backend _m2o_id hot fix 2026-05-04).
  // Coerce anything that isn't a finite positive int to null and gate
  // the query so we never build `?vendor_id=false`.
  const safeId =
    typeof vendorId === 'number' && Number.isFinite(vendorId) && vendorId > 0
      ? vendorId
      : null
  const q = query.trim()
  const params = new URLSearchParams()
  if (safeId !== null) params.set('vendor_id', String(safeId))
  if (q) params.set('q', q)
  const url = `${BASE}/vendor-products?${params.toString()}`
  return useQuery({
    queryKey: ['cofa', 'vendor-products', safeId, q],
    queryFn: () => jsonFetch<VendorProductsResponse>(url),
    // Without a vendor id there's nothing to pre-filter by, but a typed
    // search should still hit the full catalog (see ChangeProductDrawer's
    // "type to search the full catalog" copy) — don't block on vendor
    // resolution alone.
    enabled: safeId !== null || q.length > 0,
    staleTime: 60_000,
  })
}

export interface VerifyVars {
  lotId: number
  kind: CofaKind
  /** The CofA of record the user looked at; the server refuses (409) if
   *  the lot's CofA changed in the meantime. */
  documentId: number
  note?: string
}

/** Regulatory/admin: confirm this exact CofA is right for the lot. */
export function useVerifyCofa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: VerifyVars) =>
      jsonFetch<unknown>(`${BASE}/cofa/lot/${vars.lotId}/verify`, {
        method: 'POST',
        body: { kind: vars.kind, document_id: vars.documentId, note: vars.note || null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    },
  })
}

export function useUnverifyCofa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { lotId: number; kind: CofaKind }) =>
      jsonFetch<unknown>(
        `${BASE}/cofa/lot/${vars.lotId}/verify?kind=${encodeURIComponent(vars.kind)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    },
  })
}
